import React, { useEffect, useMemo, useState } from "react";
import ReactDom from "react-dom";
import { SideMenu, SideMenuProps } from "./SideMenu";
import { TopMenu, TopMenuProps } from "./TopMenu";
import { MainPageSubMenu, MainPageSubMenuProps } from "./MainPageSubMenu";
import { MainPageLayoutItem, WindowsSystemBtnItem } from "./types";
import "./index.less";
import { useLocation } from "react-router-dom";
import { ChatSideBar } from "../ChatListLayout/ChatSideBar";
import { AvatarComProps } from "../AvatarCom";
import { useFriendStore, useGlobalStore } from "@obsbot/remo-stores";
import useWebSocketStore from "@obsbot/remo-stores/src/ws-store";
import { LoadingOutlined } from "@ant-design/icons";
import { Spin } from "antd";
import { setIsOpenSpin } from "@obsbot/remo-server-api";

export interface MainPageLayoutProps
    extends SideMenuProps,
        TopMenuProps,
        Partial<Omit<MainPageSubMenuProps, "onClick" | "activeKeys">> {
    isShowOverlay: boolean;
    onClick: (mainPageLayoutItem: MainPageLayoutItem) => void;
    /** a list of keys to highlight the items */
    activeKeys: string[];
    onClickSystemBtn?: (winSystemBtn: WindowsSystemBtnItem) => void;
    onClickChatSideBar: (key: string, userInfo?: AvatarComProps) => void;
    onHandleLeave?: () => void;
    toSetting?: (toVideo?: boolean) => void;
    children?: React.ReactNode;
}

const loadingRoot = document.getElementById("loading-root") || createLoadingRoot();

function createLoadingRoot(): HTMLDivElement {
    const div = document.createElement("div");
    div.id = "loading-root";
    document.body.appendChild(div);
    return div;
}

export const MainPageLayout: React.FC<MainPageLayoutProps> = ({
    isShowOverlay,
    subMenu,
    activeKeys,
    sideMenuActiveKey,
    onClick,
    onSideMenuClick,
    children,
    onClickSystemBtn,
    onClickChatSideBar,
    handlePersonalSetting,
    handleQuitAccount,
    onHandleLeave,
    handleOverlay,
    toSetting,
}) => {
    const location = useLocation();
    const [sideBar, setSideBar] = useState(location.pathname.split("/")[1]);

    // 判断是否为登录页面或游戏覆盖布局设置页
    const isLoginPage = location.pathname === "/login";
    const isOverlayLayoutSetting = location.pathname === "/screen/game-overlay-setting";
    const { userInfo } = useGlobalStore();
    const { conversation } = useFriendStore();
    const [isSpin, setIsSpin] = useState(false);
    useEffect(() => {
        setIsOpenSpin(setIsSpin);
    }, []);
    const testWsUrl = useMemo(
        () => process.env.REMO_WS_SERVER + "/ws/" + userInfo?.user_id,
        [userInfo?.user_id],
    );
    const { connect, disconnect, isConnected, sendMessage } = useWebSocketStore();

    useEffect(() => {
        const tempList = conversation.conversationList
            .filter(e => e.type === "C2C")
            .map(item => item.userProfile?.userID);
        if (isConnected && tempList.length > 0) {
            sendMessage(
                JSON.stringify({
                    event: "online",
                    payload: {
                        list: tempList,
                    },
                }),
            );
        }
        if (isConnected) {
            sendMessage(
                JSON.stringify({
                    event: "intimate",
                }),
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isConnected, conversation.conversationList.length]);

    useEffect(() => {
        const tempSideBar = location.pathname.split("/")[1];
        setSideBar(tempSideBar);
        // 组件挂载时尝试连接WebSocket
        if (tempSideBar !== "login" && !isConnected) {
            connect(testWsUrl, {
                reconnectLimit: 3,
                heartbeatInterval: 10000,
            });
        }
        if (tempSideBar === "login") {
            // 登录页面不连接WebSocket
            disconnect();
        }
        return () => {
            if (tempSideBar === "login") {
                disconnect();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, testWsUrl, isConnected]);

    // 如果是登录页面或游戏覆盖布局设置页，直接返回 children
    if (isLoginPage || isOverlayLayoutSetting) {
        return <>{children}</>;
    }

    return (
        <div className="main-page-layout main-page-layout--spilt">
            <SideMenu
                handlePersonalSetting={handlePersonalSetting}
                handleQuitAccount={handleQuitAccount}
                sideMenuActiveKey={sideMenuActiveKey}
                onSideMenuClick={onSideMenuClick}
            />
            <div className="main-page-layout-main">
                <TopMenu
                    handleOverlay={handleOverlay}
                    overlay={isShowOverlay}
                    onClickSystemBtn={onClickSystemBtn}
                />
                <div className="main-page-layout-content main-page-layout--spilt fancy-scrollbar">
                    {ReactDom.createPortal(
                        <Spin
                            fullscreen
                            indicator={<LoadingOutlined spin />}
                            size="large"
                            spinning={isSpin}
                        />,
                        loadingRoot,
                    )}
                    {subMenu && sideBar === "setting" && (
                        <MainPageSubMenu
                            activeKeys={activeKeys}
                            subMenu={subMenu}
                            onClick={onClick}
                        />
                    )}
                    {sideBar === "friends" && (
                        <ChatSideBar
                            toSetting={toSetting}
                            onClick={onClickChatSideBar}
                            onHandleLeave={onHandleLeave}
                        />
                    )}
                    <div className="main-page-layout-inner fancy-scrollbar">
                        <>{children}</>
                    </div>
                </div>
            </div>
            <div className="lightShadowLeftTop"></div>
            <div className="lightShadowMiddleBottom"></div>
        </div>
    );
};
